import React, { useState, useEffect } from 'react';
import eventAPI from '../services/eventAPI';
import { toLocalISOString } from '../utils/dateUtils';
import RetroInput from './ui/RetroInput';
import RetroTextarea from './ui/RetroTextarea';
import RetroDateTimePicker from './ui/RetroDateTimePicker';
import RoleBadge from './RoleBadge';
import './EventModal.css';

const COLORS = [
  { name: 'Lila', value: 'purple' },
  { name: 'Pink', value: 'pink' },
  { name: 'Blau', value: 'blue' },
  { name: 'Grün', value: 'green' },
  { name: 'Orange', value: 'orange' },
  { name: 'Rot', value: 'red' },
];

function EventModal({ onClose, onEventCreated, currentUser, initialDate }) {
  const [activeTab, setActiveTab] = useState('direct'); // 'direct' or 'planning'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);

  // Common fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('purple');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Direct event fields
  const [datetime, setDatetime] = useState('');
  const [location, setLocation] = useState('');

  // Set initial date if provided
  useEffect(() => {
    if (initialDate) {
      // Set time to current time + 1 hour
      const date = new Date(initialDate);
      const now = new Date();
      date.setHours(now.getHours() + 1);
      date.setMinutes(0);
      date.setSeconds(0);
      setDatetime(toLocalISOString(date));
    }
  }, [initialDate]);

  // Planning event fields
  const [votingDeadline, setVotingDeadline] = useState('');
  const [datetimeOptions, setDatetimeOptions] = useState(['']);
  const [locationOptions, setLocationOptions] = useState(['']);

  useEffect(() => {
    loadFamilyMembers();
  }, []);

  const loadFamilyMembers = async () => {
    try {
      const data = await eventAPI.getFamilyMembers();
      setFamilyMembers(data.family_members || []);
    } catch (error) {
      console.error('Failed to load family members:', error);
      setError('Konnte Familienmitglieder nicht laden');
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      const isCurrentlySelected = prev.includes(userId);

      if (isCurrentlySelected) {
        // Deselecting - just remove
        return prev.filter(id => id !== userId);
      } else {
        // Clear error if successfully selecting
        setError(null);
        return [...prev, userId];
      }
    });
  };

  const addDatetimeOption = () => {
    setDatetimeOptions([...datetimeOptions, '']);
  };

  const removeDatetimeOption = (index) => {
    setDatetimeOptions(datetimeOptions.filter((_, i) => i !== index));
  };

  const updateDatetimeOption = (index, value) => {
    const newOptions = [...datetimeOptions];
    newOptions[index] = value;
    setDatetimeOptions(newOptions);
  };

  const addLocationOption = () => {
    setLocationOptions([...locationOptions, '']);
  };

  const removeLocationOption = (index) => {
    setLocationOptions(locationOptions.filter((_, i) => i !== index));
  };

  const updateLocationOption = (index, value) => {
    const newOptions = [...locationOptions];
    newOptions[index] = value;
    setLocationOptions(newOptions);
  };

  const validateForm = () => {
    if (!title.trim()) {
      setError('Titel ist erforderlich');
      return false;
    }

    if (selectedUsers.length === 0) {
      setError('Mindestens ein Familienmitglied muss eingeladen werden');
      return false;
    }

    if (activeTab === 'direct') {
      if (!datetime) {
        setError('Datum und Zeit sind erforderlich');
        return false;
      }
    } else {
      const validDatetimeOptions = datetimeOptions.filter(opt => opt.trim());
      if (validDatetimeOptions.length < 1) {
        setError('Mindestens 1 Datum/Zeit Option ist erforderlich');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const eventData = {
        title: title.trim(),
        description: description.trim(),
        color,
        duration_minutes: parseInt(durationMinutes),
        invited_user_ids: selectedUsers,
        event_type: activeTab
      };

      if (activeTab === 'direct') {
        eventData.datetime = datetime;
        if (location.trim()) {
          eventData.location = location.trim();
        }
      } else {
        // Planning event
        eventData.datetime_options = datetimeOptions.filter(opt => opt.trim());

        const validLocationOptions = locationOptions.filter(opt => opt.trim());
        if (validLocationOptions.length > 0) {
          eventData.location_options = validLocationOptions;
        }

        if (votingDeadline) {
          eventData.voting_deadline = votingDeadline;
        }
      }

      await eventAPI.createEvent(eventData);
      onEventCreated();
      onClose();
    } catch (error) {
      console.error('Failed to create event:', error);
      setError(error.response?.data?.error || 'Event konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content event-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">NEUES EVENT ERSTELLEN</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="event-modal-content">
          {/* Tabs */}
          <div className="event-tabs">
          <button
            className={`event-tab ${activeTab === 'direct' ? 'active' : ''}`}
            onClick={() => setActiveTab('direct')}
          >
            <span className="tab-icon">[#]</span>
            FESTER TERMIN
          </button>
          <button
            className={`event-tab ${activeTab === 'planning' ? 'active' : ''}`}
            onClick={() => setActiveTab('planning')}
          >
            <span className="tab-icon">[V]</span>
            TERMIN ABSTIMMEN
          </button>
        </div>

        <form onSubmit={handleSubmit} className="event-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">[!]</span>
              {error}
            </div>
          )}

          {/* Common Fields */}
          <div className="form-section">
            <label className="form-label">Titel *</label>
            <RetroInput
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Schwimmbad am Samstag"
              maxLength={255}
              required
            />
          </div>

          <div className="form-section">
            <label className="form-label">Beschreibung</label>
            <RetroTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Weitere Details..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-section">
              <label className="form-label">Dauer (Minuten)</label>
              <RetroInput
                type="number"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                min="15"
                step="15"
              />
            </div>

            <div className="form-section">
              <label className="form-label">Farbe</label>
              <div className="color-selector">
                {COLORS.map(c => (
                  <button
                    key={c.value}
                    type="button"
                    className={`color-button color-${c.value} ${color === c.value ? 'selected' : ''}`}
                    onClick={() => setColor(c.value)}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Direct Event Fields */}
          {activeTab === 'direct' && (
            <>
              <div className="form-section">
                <label className="form-label">Datum & Zeit *</label>
                <RetroDateTimePicker
                  value={datetime}
                  onChange={(e) => setDatetime(e.target.value)}
                  required
                />
              </div>

              <div className="form-section">
                <label className="form-label">Ort</label>
                <RetroInput
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="z.B. Hallenbad Zentrum"
                />
              </div>
            </>
          )}

          {/* Planning Event Fields */}
          {activeTab === 'planning' && (
            <>
              <div className="form-section">
                <label className="form-label">
                  Abstimmungs-Deadline
                  <span className="label-hint">(optional)</span>
                </label>
                <RetroDateTimePicker
                  value={votingDeadline}
                  onChange={(e) => setVotingDeadline(e.target.value)}
                />
              </div>

              <div className="form-section">
                <label className="form-label">Datum/Zeit Optionen * (mind. 1)</label>
                <div className="options-list">
                  {datetimeOptions.map((option, index) => (
                    <div key={index} className="option-row">
                      <RetroDateTimePicker
                        value={option}
                        onChange={(e) => updateDatetimeOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                      />
                      {datetimeOptions.length > 1 && (
                        <button
                          type="button"
                          className="option-remove-btn"
                          onClick={() => removeDatetimeOption(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="add-option-btn"
                  onClick={addDatetimeOption}
                >
                  + WEITERE OPTION
                </button>
              </div>

              <div className="form-section">
                <label className="form-label">
                  Ort Optionen
                  <span className="label-hint">(optional)</span>
                </label>
                <div className="options-list">
                  {locationOptions.map((option, index) => (
                    <div key={index} className="option-row">
                      <RetroInput
                        value={option}
                        onChange={(e) => updateLocationOption(index, e.target.value)}
                        placeholder={`Ort ${index + 1}`}
                      />
                      {locationOptions.length > 1 && (
                        <button
                          type="button"
                          className="option-remove-btn"
                          onClick={() => removeLocationOption(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="add-option-btn"
                  onClick={addLocationOption}
                >
                  + WEITERER ORT
                </button>
              </div>
            </>
          )}

          {/* User Selection */}
          <div className="form-section">
            <label className="form-label">Einladen * (mind. 1)</label>
            <div className="user-selector">
              {familyMembers.length === 0 ? (
                <p className="no-users">Keine Familienmitglieder verfügbar</p>
              ) : (
                familyMembers.map(member => (
                  <label key={member.id} className="user-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(member.id)}
                      onChange={() => handleUserToggle(member.id)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="user-info">
                      <span className="user-name">{member.username}</span>
                      <span className="user-roles">
                        {member.groups && member.groups.map(group => (
                          <RoleBadge key={group} role={group} size="small" />
                        ))}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="modal-actions">
            <button
              type="button"
              className="modal-button modal-button-secondary"
              onClick={onClose}
              disabled={loading}
            >
              ABBRECHEN
            </button>
            <button
              type="submit"
              className="modal-button modal-button-primary"
              disabled={loading}
            >
              {loading ? 'ERSTELLE...' : 'EVENT ERSTELLEN'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

export default EventModal;
